import React, { Component } from "react";
import { Button, Drawer, Icon, message } from "antd";

/**
 * OzonetelAgentConsole
 *
 * Persistent iframe-based integration with the Ozonetel CloudAgent toolbar
 * (ported from the collection portal). It mimics the Chrome-extension flow via
 * postMessage and exposes a global `window.OzonetelBridge` so any component
 * (e.g. the Credit Checker call button) can open the drawer / dial / hang up.
 *
 * Mount this once, high in the app shell, so the agent stays logged in and the
 * call survives route changes.
 */
class OzonetelAgentConsole extends Component {
    constructor(props) {
        super(props);
        this.state = {
            drawerVisible: false,
            callStatus: "Idle", // Idle, Ringing, Connected
            // Details of the number currently being dialled, shown in the
            // floating status popup. Null when no outbound call is in progress.
            callInfo: null, // { number, name }
            iframeLoaded: false,
            toolbarUrl:
                localStorage.getItem("ozonetel_toolbar_url") ||
                "https://agent.cloudagent.ozonetel.com/"
        };
        this.iframeRef = React.createRef();
        this.standaloneWindow = null;
    }

    componentDidMount() {
        window.addEventListener("message", this.handleIframeMessage);
        document.addEventListener("mousedown", this.handleClickOutside);
        // Expose a global method for other components to trigger dials/hangups.
        window.OzonetelBridge = {
            dial: this.dial,
            hangup: this.hangup,
            open: () => this.setState({ drawerVisible: true }),
            // Lets callers (e.g. the Credit Checker call button) populate the
            // status popup with the number/name being dialled.
            setCallInfo: info =>
                this.setState({ callInfo: info, callStatus: "Idle" }),
            clearCallInfo: () => this.setState({ callInfo: null })
        };
    }

    componentWillUnmount() {
        window.removeEventListener("message", this.handleIframeMessage);
        document.removeEventListener("mousedown", this.handleClickOutside);
        delete window.OzonetelBridge;
    }

    handleClickOutside = event => {
        // If drawer is closed, do nothing.
        if (!this.state.drawerVisible) {
            return;
        }

        // Don't close if clicking the toggle button itself.
        const toggleButton = document.querySelector(
            ".ozonetel-floating-trigger"
        );
        if (toggleButton && toggleButton.contains(event.target)) {
            return;
        }

        // Close only when the click is outside the open Ant Design drawer.
        const drawer = document.querySelector(
            ".ant-drawer-right.ant-drawer-open"
        );
        if (drawer && !drawer.contains(event.target)) {
            this.setState({ drawerVisible: false });
        }
    };

    handleIframeMessage = event => {
        // Validation: ensure the message comes from the Ozonetel domain.
        if (!event.origin.includes("ozonetel.com")) {
            return;
        }

        const data = event.data;
        if (data && (data.type || data.action)) {
            const msgType = data.type || data.action;
            switch (msgType) {
            case "login":
                console.log("Ozonetel Login successful.");
                break;
            case "callStarted":
            case "busyAgent":
                window.isOzonetelCallActive = true;
                this.setState({ callStatus: "Connected" });
                break;
            case "callEnded":
            case "dropCall":
            case "agentRelease":
            case "agentLoggedOut":
                window.isOzonetelCallActive = false;
                this.setState({ callStatus: "Idle", callInfo: null });
                this.setState({ callStatus: "Idle" });
                break;
            case "ringing":
                this.setState({ callStatus: "Ringing" });
                break;
            default:
                break;
            }
        }
    };

    sendMessage = msg => {
        // Target iframe.
        if (this.iframeRef.current && this.iframeRef.current.contentWindow) {
            this.iframeRef.current.contentWindow.postMessage(msg, "*");
        }

        // Target standalone window.
        if (this.standaloneWindow && !this.standaloneWindow.closed) {
            this.standaloneWindow.postMessage(msg, "*");
        }

        if (
            !this.iframeRef.current &&
            (!this.standaloneWindow || this.standaloneWindow.closed)
        ) {
            message.error(
                "Ozonetel Toolbar is not loaded. Please open the console first."
            );
        }
    };

    launchStandalone = () => {
        const { toolbarUrl } = this.state;
        const width = 380;
        const height = 600;
        const left = window.screen.width / 2 - width / 2;
        const top = window.screen.height / 2 - height / 2;

        this.standaloneWindow = window.open(
            toolbarUrl,
            "OzonetelAgentConsole",
            `width=${width},height=${height},left=${left},top=${top},menubar=no,status=no,toolbar=no`
        );

        if (!this.standaloneWindow) {
            message.error("Popup blocked! Please allow popups for this site.");
        } else {
            message.success("Ozonetel Console launched in standalone window.");
        }
    };

    dial = number => {
        // Open the drawer first so the iframe is rendered and ready.
        if (!this.state.drawerVisible) {
            this.setState({ drawerVisible: true }, () => {
                // Small timeout to allow iframe contentWindow to be ready.
                setTimeout(
                    () =>
                        this.sendMessage({ cmd: "manualDial", number: number }),
                    500
                );
            });
        } else {
            this.sendMessage({ cmd: "manualDial", number: number });
        }
    };

    hangup = () => {
        this.sendMessage({ cmd: "hangup" });
    };

    toggleDrawer = () => {
        this.setState({ drawerVisible: !this.state.drawerVisible });
    };

    onIframeLoad = () => {
        this.setState({ iframeLoaded: true });
        console.log("Ozonetel Iframe Loaded Successfully");
    };

    render() {
        const { drawerVisible, callStatus, callInfo, toolbarUrl } = this.state;

        // Status popup presentation: colour + label keyed off the live call
        // status (red once connected, like the Ozonetel toolbar).
        const STATUS_VIEW = {
            Connected: { color: "#ff4d4f", label: "Call Connected" },
            Ringing: { color: "#faad14", label: "Ringing…" },
            Idle: { color: "#1890ff", label: "Initiating call…" }
        };
        const status = STATUS_VIEW[callStatus] || STATUS_VIEW.Idle;

        return (
            <React.Fragment>
                {/* Call-status panel — slides in from the right while a call
                    initiated from the app is in progress. */}
                <Drawer
                    title={null}
                    placement="right"
                    width={320}
                    closable={false}
                    mask={false}
                    visible={Boolean(callInfo)}
                    zIndex={1101}
                    bodyStyle={{ padding: 0 }}
                >
                    {callInfo ? (
                        <div>
                            <div
                                style={{
                                    background: status.color,
                                    color: "#fff",
                                    padding: "16px 20px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between"
                                }}
                            >
                                <span
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        fontSize: 15,
                                        fontWeight: 700
                                    }}
                                >
                                    <Icon
                                        type="phone"
                                        theme="filled"
                                        style={{ marginRight: 10, fontSize: 18 }}
                                    />
                                    {status.label}
                                </span>
                                {callStatus !== "Idle" ? (
                                    <Icon
                                        type="loading"
                                        style={{ fontSize: 16 }}
                                    />
                                ) : null}
                            </div>
                            <div style={{ padding: "20px" }}>
                                {callInfo.name ? (
                                    <div
                                        style={{
                                            fontSize: 16,
                                            fontWeight: 600,
                                            color: "#1f1f1f",
                                            marginBottom: 4
                                        }}
                                    >
                                        {callInfo.name}
                                    </div>
                                ) : null}
                                <div
                                    style={{
                                        fontSize: 20,
                                        fontWeight: 700,
                                        letterSpacing: "0.5px",
                                        color: "#262626"
                                    }}
                                >
                                    {callInfo.number}
                                </div>
                                <div
                                    style={{
                                        marginTop: 6,
                                        fontSize: 12,
                                        color: "#8c8c8c"
                                    }}
                                >
                                    via Ozonetel
                                </div>
                                <div style={{ marginTop: 24 }}>
                                    <Button
                                        type="danger"
                                        block
                                        icon="phone"
                                        onClick={this.hangup}
                                    >
                                        Hang up
                                    </Button>
                                    <Button
                                        block
                                        style={{ marginTop: 10 }}
                                        onClick={() =>
                                            this.setState({ callInfo: null })
                                        }
                                    >
                                        Close
                                    </Button>
                                </div>
                            </div>
                        </div>
                    ) : null}
                </Drawer>
                {/* Floating trigger button — shown only where the host opts in
                    (Credit Checker tab). The console stays mounted regardless so
                    an in-progress call survives route changes. */}
                {this.props.showTrigger !== false && (
                <div
                    className="ozonetel-floating-trigger"
                    style={{
                        position: "fixed",
                        bottom: 85,
                        right: 20,
                        // Above the full-screen case-details Ant Drawer (z-index 1000)
                        // so the trigger stays reachable from inside a case.
                        zIndex: 1100
                    }}
                >
                    <Button
                        type={callStatus !== "Idle" ? "danger" : "primary"}
                        shape="circle"
                        size="large"
                        onClick={this.toggleDrawer}
                        style={{
                            boxShadow: "0 4px 10px rgba(0,0,0,0.3)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor:
                                callStatus === "Connected"
                                    ? "#ff4d4f"
                                    : callStatus === "Ringing"
                                        ? "#faad14"
                                        : "#1890ff",
                            borderColor:
                                callStatus === "Connected"
                                    ? "#ff4d4f"
                                    : callStatus === "Ringing"
                                        ? "#faad14"
                                        : "#1890ff"
                        }}
                    >
                        <Icon
                            type="customer-service"
                            style={{ fontSize: "24px", color: "#fff" }}
                        />
                    </Button>
                    {callStatus !== "Idle" && (
                        <div
                            style={{
                                position: "absolute",
                                top: -10,
                                right: -5,
                                backgroundColor: "#fff",
                                borderRadius: "50%",
                                padding: "2px"
                            }}
                        >
                            <Icon
                                type="loading"
                                style={{ color: "#ff4d4f" }}
                            />
                        </div>
                    )}
                </div>
                )}

                {/* Persistent drawer for the toolbar. */}
                <Drawer
                    title={
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                width: "100%"
                            }}
                        >
                            <span>Ozonetel Agent Console</span>
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center"
                                }}
                            >
                                <span
                                    style={{
                                        fontSize: "10px",
                                        color: "#888",
                                        marginRight: "10px"
                                    }}
                                >
                                    {callStatus}
                                </span>
                                <Button
                                    size="small"
                                    icon="export"
                                    onClick={this.launchStandalone}
                                    title="Open in standalone window (recommended if iframe is blocked)"
                                />
                            </div>
                        </div>
                    }
                    placement="right"
                    width={380}
                    closable={true}
                    onClose={this.toggleDrawer}
                    visible={drawerVisible}
                    forceRender={true}
                    mask={false} // Allow interaction with the rest of the app.
                    // Above the full-screen case-details Drawer (z-index 1000).
                    zIndex={1100}
                    bodyStyle={{
                        padding: 0,
                        height: "calc(100% - 55px)",
                        overflow: "hidden"
                    }}
                >
                    <div
                        style={{
                            width: "100%",
                            height: "100%",
                            position: "relative"
                        }}
                    >
                        <style>{`
                            @keyframes pulse-red {
                                0% { background-color: #fff1f0; box-shadow: 0 0 0 0 rgba(255, 77, 79, 0.4); }
                                50% { background-color: #ffccc7; box-shadow: 0 0 0 10px rgba(255, 77, 79, 0); }
                                100% { background-color: #fff1f0; box-shadow: 0 0 0 0 rgba(255, 77, 79, 0); }
                            }
                            .shining-warning {
                                animation: pulse-red 2s infinite;
                            }
                        `}</style>
                        {callStatus !== "Idle" && (
                            <div
                                className="shining-warning"
                                style={{
                                    backgroundColor: "#fff1f0",
                                    border: "1px solid #ff4d4f",
                                    padding: "8px 12px",
                                    display: "flex",
                                    alignItems: "center",
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    zIndex: 10,
                                    boxShadow: "0 2px 8px rgba(255, 77, 79, 0.3)"
                                }}
                            >
                                <Icon
                                    type="alert"
                                    theme="filled"
                                    style={{
                                        color: "#ff4d4f",
                                        marginRight: "8px",
                                        fontSize: "18px"
                                    }}
                                />
                                <span
                                    style={{
                                        fontSize: "12px",
                                        fontWeight: "900",
                                        color: "#cf1322",
                                        textTransform: "uppercase",
                                        letterSpacing: "0.5px"
                                    }}
                                >
                                    Active Call! RELOADING WILL DISCONNECT THE
                                    CALL
                                </span>
                            </div>
                        )}
                        <iframe
                            ref={this.iframeRef}
                            src={toolbarUrl}
                            title="Ozonetel Toolbar"
                            onLoad={this.onIframeLoad}
                            style={{
                                width: "100%",
                                height:
                                    "calc(100% - " +
                                    (callStatus !== "Idle" ? "40px" : "0px") +
                                    ")",
                                border: "none",
                                marginTop:
                                    callStatus !== "Idle" ? "40px" : "0px",
                                transition: "all 0.3s"
                            }}
                            allow="microphone"
                        />
                    </div>
                </Drawer>
            </React.Fragment>
        );
    }
}

export default OzonetelAgentConsole;

